import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Layout as LayoutIcon,
  LogOut,
  User,
  Database,
  Home
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import MarkdownModal from '../common/MarkdownModal';

const Sidebar = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const tabParam = new URLSearchParams(location.search).get('tab');

  const navItems = [
    { label: 'Home', path: '/', icon: Home, isActive: location.pathname === '/' },
    { label: 'Workspaces', path: '/workspaces', icon: LayoutIcon, isActive: location.pathname === '/workspaces' },
    {
      label: 'Data Sources',
      path: '/sources',
      icon: Database,
      isActive: location.pathname === '/sources' && tabParam !== 'functions'
    },
    { label: 'Profile', path: '/profile', icon: User, isActive: location.pathname === '/profile' || location.pathname === '/billing' },
  ];

  return (
    <aside className="w-64 border-r border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 flex flex-col h-full transition-colors shrink-0">
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-center mb-10">
          <Link to="/" className="transition-transform hover:scale-105">
            <img src="/logo.svg" alt="manteo.ai" className="h-14" />
          </Link>
        </div>

        <nav className="space-y-1">
          {navItems.map((item) => {
            const isActive = item.isActive;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-2xl text-xs font-black transition-all uppercase tracking-widest",
                  isActive
                    ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 border border-emerald-100/50 dark:border-emerald-800/30"
                    : "text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50/50 dark:hover:bg-gray-900/50 border border-transparent"
                )}
              >
                <item.icon size={18} className={cn(isActive ? "text-emerald-600" : "text-gray-400")} />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="p-4 mt-auto">
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-3xl border border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-emerald-200/50">
              {user?.email?.split('@')[0]?.substring(0, 2).toUpperCase() || 'GU'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black text-gray-900 dark:text-gray-100 uppercase tracking-tight truncate leading-none mb-1">
                {user?.email || 'Guest User'}
              </p>
              <p className="text-[8px] text-emerald-600 font-bold uppercase tracking-widest leading-none">
                Pilot Plan
              </p>
            </div>
          </div>

          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[10px] font-black text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-colors uppercase tracking-widest"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </div>

      {/* Terms and Privacy Policy Links */}
      <div className="px-4 pb-4">
        <div className="text-center">
          <p className="text-[14px] text-gray-400 dark:text-gray-500">
            <button
              onClick={() => setTermsModalOpen(true)}
              className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
            >
              Terms
            </button>
            {' · '}
            <button
              onClick={() => setPrivacyModalOpen(true)}
              className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
            >
              Privacy
            </button>
          </p>
        </div>
      </div>

      <MarkdownModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        markdownUrl="/terms-of-service.md"
      />

      <MarkdownModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        markdownUrl="/privacy-policy.md"
      />
    </aside>
  );
};

export default Sidebar;
